import React, { useContext, useEffect } from "react"
import { CarrotCardCostSharePreAuthContext } from "#/components/carrot-card-cost-share/state/CarrotCardCostSharePreAuthContext"
import { Alert, Checkbox, FormControlLabel, Link, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { JourneyTypes } from "#/components/get-reimbursed/steps/ExpenseJourneyTypes"
import { FormattedMessage } from "react-intl"
import { Link as RouterLink } from "react-router-dom"
import { DbgPaths } from "#/utils/Paths"
import {
  RelatedToStorageFeesValues,
  WhoWillReceiveCareValues
} from "#/components/carrot-card-cost-share/steps/shared/stepOptionValues"

export const journeyTypeWithAlertAndCheckbox = [JourneyTypes.GC, JourneyTypes.FERTILITY_CARE, JourneyTypes.PRESERVATION]

export function AlertAndCheckbox({
  isConfirmed,
  setIsConfirmed
}: {
  isConfirmed: boolean
  setIsConfirmed: (isConfirmed: boolean) => void
}): JSX.Element {
  const {
    formState: { journeyType }
  } = useContext(CarrotCardCostSharePreAuthContext)

  function getContentByJourneyType(): JSX.Element {
    switch (journeyType) {
      case JourneyTypes.GC:
        return <GcAlertAndCheckbox isConfirmed={isConfirmed} setIsConfirmed={setIsConfirmed} />
      case JourneyTypes.FERTILITY_CARE:
        return <FertilityCareAlertAndCheckbox isConfirmed={isConfirmed} setIsConfirmed={setIsConfirmed} />
      case JourneyTypes.PRESERVATION:
        return <FertilityPreservationAlertAndCheckbox isConfirmed={isConfirmed} setIsConfirmed={setIsConfirmed} />
      default:
        return null
    }
  }

  return getContentByJourneyType()
}

function GcAlertAndCheckbox({
  isConfirmed,
  setIsConfirmed
}: {
  isConfirmed: boolean
  setIsConfirmed: (isConfirmed: boolean) => void
}): JSX.Element {
  const {
    serverState: { hasFertilityCareCoverage }
  } = useContext(CarrotCardCostSharePreAuthContext)
  const theme = useTheme()
  return (
    <>
      <Alert severity="info">
        <Stack spacing={theme.tokens.spacing.md}>
          <Typography color="inherit">
            <FormattedMessage
              defaultMessage="This total must include only <link>gestational surrogacy</link> expenses."
              values={{
                link: (linkContent) => (
                  <Link
                    color="inherit"
                    component={RouterLink}
                    to={DbgPaths.GESTATIONAL_SURROGACY_COVERAGE}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {linkContent}
                  </Link>
                )
              }}
            />
          </Typography>
          <Typography color="inherit">
            {hasFertilityCareCoverage ? (
              <FormattedMessage
                defaultMessage="<link>Fertility care</link> for intended parents must be submitted as a separate request."
                values={{
                  link: (linkContent) => (
                    <Link
                      component={RouterLink}
                      to={DbgPaths.FERTILITY_COVERAGE}
                      color="inherit"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {linkContent}
                    </Link>
                  )
                }}
              />
            ) : (
              <FormattedMessage defaultMessage="Your benefit does not cover fertility care for intended parents. " />
            )}
          </Typography>
        </Stack>
      </Alert>

      <FormControlLabel
        required
        control={<Checkbox checked={isConfirmed} onChange={(e) => setIsConfirmed(e.target.checked)} />}
        label={
          <FormattedMessage defaultMessage="I confirm that this total only includes gestational surrogacy expenses." />
        }
      />
    </>
  )
}

function FertilityCareAlertAndCheckbox({
  isConfirmed,
  setIsConfirmed
}: {
  isConfirmed: boolean
  setIsConfirmed: (isConfirmed: boolean) => void
}): JSX.Element {
  const {
    formState: { whoReceivedCare },
    serverState: { hasDonorAssistanceCoverage }
  } = useContext(CarrotCardCostSharePreAuthContext)
  const theme = useTheme()
  const memberOrPartnerReceivedCare =
    whoReceivedCare === WhoWillReceiveCareValues.ME ||
    whoReceivedCare === WhoWillReceiveCareValues.LEGAL_PARTNER ||
    whoReceivedCare === WhoWillReceiveCareValues.NON_MARRIED_PARTNER

  const donorAssistanceCoverageContent = hasDonorAssistanceCoverage ? (
    <Typography color="inherit">
      <FormattedMessage
        defaultMessage="For expenses listed under “<link>Donor assistance</link>,” please submit a separate request and select “Donor” as the person receiving care or services."
        values={{
          link: (linkContent) => (
            <Link
              component={RouterLink}
              to={DbgPaths.FERTILITY_COVERAGE}
              color="inherit"
              target="_blank"
              rel="noopener noreferrer"
            >
              {linkContent}
            </Link>
          )
        }}
      />
    </Typography>
  ) : (
    <Typography color="inherit">
      <FormattedMessage defaultMessage="Donor assistance expenses are not covered by your benefit. " />
    </Typography>
  )

  return (
    <>
      <Alert severity="info">
        {memberOrPartnerReceivedCare ? (
          <Stack spacing={theme.tokens.spacing.md}>
            <Typography color="inherit">
              <FormattedMessage
                defaultMessage="This total must include only {fertilityCareReceived} <link>fertility care</link> expenses."
                values={{
                  fertilityCareReceived: whoReceivedCare === WhoWillReceiveCareValues.ME ? "your" : "your partner's",
                  link: (linkContent) => (
                    <Link
                      component={RouterLink}
                      to={DbgPaths.FERTILITY_COVERAGE}
                      color="inherit"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </Typography>
            {donorAssistanceCoverageContent}
          </Stack>
        ) : (
          <Stack spacing={theme.tokens.spacing.md}>
            <Typography color="inherit">
              <FormattedMessage
                defaultMessage="This total must only include expenses listed under “<link>Donor assistance</link>.” "
                values={{
                  link: (linkContent) => (
                    <Link
                      component={RouterLink}
                      to={DbgPaths.FERTILITY_COVERAGE}
                      color="inherit"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </Typography>
            <Typography color="inherit">
              <FormattedMessage
                defaultMessage="Do not include any other <link>fertility care</link> expenses. These must be submitted as a separate request. "
                values={{
                  link: (linkContent) => (
                    <Link
                      component={RouterLink}
                      to={DbgPaths.FERTILITY_COVERAGE}
                      color="inherit"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {linkContent}
                    </Link>
                  )
                }}
              />
            </Typography>
          </Stack>
        )}
      </Alert>

      <FormControlLabel
        required
        control={<Checkbox checked={isConfirmed} onChange={(e) => setIsConfirmed(e.target.checked)} />}
        label={
          memberOrPartnerReceivedCare ? (
            <FormattedMessage
              defaultMessage="I confirm that this total only includes {fertilityCareReceived} fertility care expenses."
              values={{
                fertilityCareReceived: whoReceivedCare === WhoWillReceiveCareValues.ME ? "my" : "my partner's"
              }}
            />
          ) : (
            <FormattedMessage defaultMessage="I confirm that this total only includes donor assistance expenses. " />
          )
        }
      />
    </>
  )
}

function FertilityPreservationAlertAndCheckbox({
  isConfirmed,
  setIsConfirmed
}: {
  isConfirmed: boolean
  setIsConfirmed: (isConfirmed: boolean) => void
}): JSX.Element {
  const {
    formState: { isStorageFees }
  } = useContext(CarrotCardCostSharePreAuthContext)

  useEffect(() => {
    if (isStorageFees !== RelatedToStorageFeesValues.POST_FIRST_YEAR_STORAGE) {
      setIsConfirmed(true)
    }
  }, [isStorageFees, setIsConfirmed])

  return isStorageFees === RelatedToStorageFeesValues.POST_FIRST_YEAR_STORAGE ? (
    <>
      <Alert severity="info">
        <Typography color="inherit">
          <FormattedMessage defaultMessage="This total must include only long-term storage fees. Any other expenses must be submitted as a separate request. " />
        </Typography>
      </Alert>

      <FormControlLabel
        required
        control={<Checkbox checked={isConfirmed} onChange={(e) => setIsConfirmed(e.target.checked)} />}
        label={
          <FormattedMessage defaultMessage="I confirm that this total only includes storage fees after the first year." />
        }
      />
    </>
  ) : null
}
